import { createEventHook, useEventListener } from '@vueuse/core';
import { ref, shallowRef, watch, watchEffect } from 'vue';
import { DEFAULT_DRAG_DISTANCE_PX, hasExceededDragDistance } from '#layers/base/public';
import { useSharedMouse } from '#layers/ui/composables/use-shared-mouse';

export interface UseStartDragOptions {
	/**
	 * Minimum Euclidean distance to trigger drag
	 */
	distance?: number;
}

export function useStartDrag(options: UseStartDragOptions = {}) {
	const {
		distance: minDistance = DEFAULT_DRAG_DISTANCE_PX,
	} = options;

	const event = createEventHook();
	const { x: mouseX, y: mouseY } = useSharedMouse();
	const mouseDownElement = shallowRef<HTMLElement | null>(null);
	const mouseDownPosition = ref<{ x: number; y: number } | null>(null);

	watch(mouseDownElement, (element) => {
		mouseDownPosition.value = element ? { x: mouseX.value, y: mouseY.value } : null;
	});

	useEventListener('mouseup', () => (
		mouseDownElement.value = null
	));

	watchEffect(() => {
		if (mouseDownPosition.value && hasExceededDragDistance(mouseDownPosition.value, {
			x: mouseX.value,
			y: mouseY.value,
		}, minDistance)) {
			event.trigger();
			mouseDownElement.value = null;
			mouseDownPosition.value = null;
		}
	});

	return {
		mouseDownElement,
		onStartDrag: event.on,
	};
}
